import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Link2, Link2Off, ShieldCheck, ArrowRight, Info, Fingerprint, Shield, Sparkles, ExternalLink, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { isWebAuthnSupported, registerPasskey } from "../lib/webauthn";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import AlpacaCard from "../components/AlpacaCard";
import TradierCard from "../components/TradierCard";
import IBKRConnectCard from "../components/IBKRConnectCard";

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState(user?.account_mode || "paper");
  const [connecting, setConnecting] = useState(false);
  const [passkey, setPasskey] = useState(null);
  const [risk, setRisk] = useState(null);
  const [rUnit, setRUnit] = useState("pct");
  const [rValue, setRValue] = useState("2");
  const [rDailyCap, setRDailyCap] = useState("");
  const [rMinVol, setRMinVol] = useState("");
  const [rEnforceBots, setREnforceBots] = useState(true);
  const [savingRisk, setSavingRisk] = useState(false);
  const [digest, setDigest] = useState(null);
  const [digestEmail, setDigestEmail] = useState("");
  const navigate = useNavigate();

  const load = () => api.get("/ibkr/status").then((r) => setStatus(r.data)).catch(() => {});
  const loadPasskey = () => api.get("/webauthn/status").then((r) => setPasskey(r.data)).catch(() => {});
  const loadDigest = () => api.get("/settings/notification-email").then((r) => {
    setDigest(r.data);
    setDigestEmail(r.data.notification_email || "");
  }).catch(() => {});
  const loadRisk = () => api.get("/settings/risk").then((r) => {
    setRisk(r.data);
    const p = r.data.profile || {};
    setRUnit(p.unit || "pct");
    setRValue(String(p.value ?? 2));
    setRDailyCap(p.daily_loss_cap_usd == null ? "" : String(p.daily_loss_cap_usd));
    setRMinVol(p.min_volume_usd_24h == null ? "" : String(p.min_volume_usd_24h));
    setREnforceBots(p.enforce_bots !== false);
  }).catch(() => {});
  useEffect(() => { load(); loadPasskey(); loadRisk(); loadDigest(); }, []);

  useEffect(() => { setMode(user?.account_mode || "paper"); }, [user?.account_mode]);

  const changeMode = async (next) => {
    setMode(next);
    try {
      await api.patch("/auth/mode", { account_mode: next });
      await refreshUser();
      toast.success(`Account mode: ${next}`);
    } catch {
      toast.error("Failed to update mode");
    }
  };

  const connect = async (linkMode) => {
    setConnecting(true);
    try {
      const { data } = await api.post("/ibkr/connect", { mode: linkMode });
      // Send user to our mock IBKR consent screen
      navigate(data.authorize_url);
    } catch (e) {
      toast.error("Failed to start IBKR connect");
    } finally { setConnecting(false); }
  };

  const disconnect = async () => {
    await api.post("/ibkr/disconnect");
    toast.success("IBKR account disconnected");
    load();
  };

  const connected = status?.connected;

  return (
    <div className="space-y-8 max-w-4xl" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Preferences</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Settings</h1>
      </div>

      {/* Account mode */}
      <Card className="p-6 border-border rounded-xl">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-display font-semibold">Account mode</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              Choose which account(s) you're operating. Paper is safe practice with virtual cash.
              Live routes real orders through IBKR (only when connected).
            </p>
          </div>
          <Tabs value={mode} onValueChange={changeMode}>
            <TabsList data-testid="mode-tabs">
              <TabsTrigger value="paper" data-testid="mode-paper">Paper</TabsTrigger>
              <TabsTrigger value="live" data-testid="mode-live">Live</TabsTrigger>
              <TabsTrigger value="both" data-testid="mode-both">Both</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        {mode === "live" && !connected && (
          <div className="mt-4 p-3 rounded-lg bg-[hsl(var(--ai))]/10 border border-[hsl(var(--ai))]/25 text-sm flex gap-2">
            <Info className="w-4 h-4 mt-0.5 text-[hsl(var(--ai))]" />
            <div>Live mode is selected but no IBKR account is connected. Connect below to enable real orders.</div>
          </div>
        )}
      </Card>

      {/* Weekly digest email */}
      <Card className="p-6 border-border rounded-xl" data-testid="digest-email-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-display font-semibold flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[hsl(var(--ai))]" /> Weekly digest email
            </div>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              Every Sunday, get your grade + coaching in your inbox so Monday opens already sharp.
            </p>
          </div>
          {digest?.resend_configured ? (
            <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--gain))] flex items-center gap-1" data-testid="resend-status">
              Sending live via Resend
            </span>
          ) : (
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground" data-testid="resend-status">
              Preview mode
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          <div>
            <Label>Notification email</Label>
            <div className="flex gap-2 mt-1">
              <Input type="email" value={digestEmail} onChange={(e) => setDigestEmail(e.target.value)}
                     placeholder="you@example.com" className="font-mono-tab" data-testid="digest-email-input" />
              <Button variant="outline" size="sm" className="rounded-full"
                      data-testid="digest-email-save"
                      onClick={async () => {
                        try {
                          await api.patch("/settings/notification-email", { email: digestEmail });
                          toast.success("Email saved");
                          loadDigest();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Save failed");
                        }
                      }}>Save</Button>
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              We'll send the digest here every Sunday once you add a Resend API key (RESEND_API_KEY in backend .env).
            </div>
          </div>
          <div className="flex flex-col justify-end gap-2">
            <a href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/journal/weekly/email-preview`}
               target="_blank" rel="noreferrer"
               data-testid="digest-preview-link"
               className="text-sm text-center inline-flex items-center justify-center gap-2 rounded-full border border-border h-10 hover:bg-secondary transition-colors">
              Preview digest
            </a>
            <Button
              onClick={async () => {
                try {
                  const r = await api.post("/journal/weekly/email-send");
                  toast.success(`Digest sent to ${r.data.to}`);
                  loadDigest();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Send failed");
                }
              }}
              data-testid="digest-send-now"
              disabled={!digest?.resend_configured}
              className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-10">
              Send now {digest?.resend_configured ? "" : "(needs Resend key)"}
            </Button>
            {digest?.last_digest_sent_at && (
              <div className="text-[11px] text-muted-foreground text-center">
                Last sent {new Date(digest.last_digest_sent_at).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Risk profile */}
      <Card className="p-6 border-border rounded-xl" data-testid="risk-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-display font-semibold flex items-center gap-2"><Shield className="w-4 h-4" /> Risk defaults</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              How much to risk on any single trade, and an optional daily loss cutoff to auto-stop bots for the day.
            </p>
          </div>
          {risk && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Equity</div>
              <div className="font-mono-tab text-lg" data-testid="risk-equity">${Math.round(risk.equity).toLocaleString()}</div>
              <div className={`text-xs mt-0.5 ${risk.realized_today >= 0 ? "gain" : "loss"}`} data-testid="risk-realized-today">
                Today: {risk.realized_today >= 0 ? "+" : ""}${risk.realized_today.toFixed(2)}
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-5">
          <div>
            <Label>Risk unit</Label>
            <Tabs value={rUnit} onValueChange={setRUnit} className="mt-1">
              <TabsList data-testid="risk-unit-tabs">
                <TabsTrigger value="pct" data-testid="risk-unit-pct">% of equity</TabsTrigger>
                <TabsTrigger value="usd" data-testid="risk-unit-usd">$ per trade</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div>
            <Label>{rUnit === "pct" ? "Max risk per trade (%)" : "Max risk per trade ($)"}</Label>
            <Input type="number" min="0" step={rUnit === "pct" ? "0.1" : "10"}
                   value={rValue} onChange={(e) => setRValue(e.target.value)}
                   className="mt-1 font-mono-tab" data-testid="risk-value" />
            <div className="text-[11px] text-muted-foreground mt-1">
              {rUnit === "pct" && risk?.equity ? `≈ $${(risk.equity * (parseFloat(rValue) || 0) / 100).toFixed(0)} at current equity` : "How much you're willing to lose if the stop is hit"}
            </div>
          </div>
          <div>
            <Label>Daily loss cutoff ($)</Label>
            <Input type="number" min="0" step="10" placeholder="Off"
                   value={rDailyCap} onChange={(e) => setRDailyCap(e.target.value)}
                   className="mt-1 font-mono-tab" data-testid="risk-daily-cap" />
            <div className="text-[11px] text-muted-foreground mt-1">Leave empty to disable. Auto-stops bots once today's realized P&amp;L hits −$this.</div>
          </div>
          <div>
            <Label>Min 24h $ volume</Label>
            <Input type="number" min="0" step="100000" placeholder="Off"
                   value={rMinVol} onChange={(e) => setRMinVol(e.target.value)}
                   className="mt-1 font-mono-tab" data-testid="risk-min-volume" />
            <div className="text-[11px] text-muted-foreground mt-1">
              Skip trades on illiquid names. e.g. 1000000 = only trade if 24h $ volume ≥ $1M.
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <input type="checkbox" checked={rEnforceBots} onChange={(e) => setREnforceBots(e.target.checked)}
                   data-testid="risk-enforce-bots" className="rounded" />
            Enforce these limits on auto-trading bots
          </label>
          <Button
            onClick={async () => {
              setSavingRisk(true);
              try {
                const body = {
                  unit: rUnit,
                  value: parseFloat(rValue) || 0,
                  daily_loss_cap_usd: rDailyCap === "" ? null : Math.max(0, parseFloat(rDailyCap) || 0),
                  min_volume_usd_24h: rMinVol === "" ? null : Math.max(0, parseFloat(rMinVol) || 0),
                  enforce_bots: rEnforceBots,
                };
                await api.patch("/settings/risk", body);
                toast.success("Risk defaults saved");
                loadRisk();
              } catch (e) {
                toast.error("Failed to save risk defaults");
              } finally {
                setSavingRisk(false);
              }
            }}
            className="rounded-full bg-foreground text-background hover:bg-foreground/90"
            data-testid="risk-save" disabled={savingRisk}>
            {savingRisk ? "Saving…" : "Save risk defaults"}
          </Button>
        </div>
      </Card>

      {/* Tradier — fastest same-day path to real broker data */}
      <TradierCard />

      {/* Alpaca broker connection — REAL data, easiest setup */}
      <AlpacaCard />

      {/* IBKR MCP native connection — the new preferred path (no droplet/gateway) */}
      <IBKRConnectCard />

      {/* Face ID / passkey */}
      <Card className="p-6 border-border rounded-xl" data-testid="passkey-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
              <Fingerprint className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display font-semibold">Face ID / Passkey</div>
              <p className="text-sm text-muted-foreground mt-1 max-w-lg">
                Use Face ID, Touch ID, or your device passkey to sign in without typing your password.
                Stored securely on this device.
              </p>
              {passkey?.enabled && (
                <div className="mt-2 text-xs text-muted-foreground">
                  {passkey.n_credentials} passkey{passkey.n_credentials === 1 ? "" : "s"} enrolled
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 min-w-[200px]">
            {!isWebAuthnSupported() ? (
              <div className="text-xs text-muted-foreground text-right">Passkeys aren't supported on this device.</div>
            ) : passkey?.enabled ? (
              <Button variant="outline" onClick={async () => {
                await api.delete("/webauthn/credentials");
                toast.success("Passkey removed");
                loadPasskey();
              }} className="rounded-full" data-testid="passkey-remove">
                Remove passkey
              </Button>
            ) : (
              <Button onClick={async () => {
                try {
                  await registerPasskey();
                  toast.success("Face ID enabled");
                  loadPasskey();
                } catch (e) { toast.error(e?.message || "Enrollment cancelled"); }
              }} className="rounded-full bg-foreground text-background hover:bg-foreground/90"
                 data-testid="passkey-enable">
                <Fingerprint className="w-4 h-4 mr-2" /> Enable Face ID
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
